import fb from '@/firebaseConfig';
import router from '@/router';
import { Plugins } from '@capacitor/core';
import localStore from '@/utils/LocalStore';
import analytics from '@/utils/Analytics';

const { AudioPlugin } = Plugins;

// TODO
// Player error
// Bluetooth permission - don't think I need this
// OFFLINE

/**
 * NOTES
 * - For AudioCards, interactions with routes happen in two places:
 *    - If the player changes IDs, the event listener will change the route to match
 *    - If the route changes within Vue, the player stops if changing cards or leaving audio route
 * - For Stacks, don't change routes when tracks change
 */

/*

  HELPERS

 */

const getTrackData = ({ id, audioById, allCards, defaultAudioTag }) => {
  const silentTrackUrl = audioById['silent-track-2s'][defaultAudioTag].url;
  const audio = audioById[id] || {};
  const card = allCards[id] || {};
  // TODO If more track variants available, update to use other tags
  const uri = audio[defaultAudioTag] ? audio[defaultAudioTag].url : silentTrackUrl;

  return {
    mediaId: id,
    title: card.title || '',
    description: card.subtitle || '',
    uri,
    category: card.category || 'journey',
  };
};

// Generate IDs for playlist - repeats IDs if target length longer than set
const generatePlaylist = ({
  set,
  startIndex,
  targetLength,
  audioById,
  allCards,
  defaultAudioTag,
}) => {
  const playlistIds = [];
  let i = startIndex;
  while (playlistIds.length < targetLength) {
    playlistIds.push(set[(i + set.length) % set.length]);
    i += 1;
  }
  const playlistData = playlistIds.map(id =>
    getTrackData({ id, audioById, allCards, defaultAudioTag })
  );
  return { playlistIds, playlistData };
};

/*

  STATE

 */

const state = {
  playlistIds: [],
  currentTrackId: '',
  currentTrackIndex: 0,
  isPlaying: false,
  isLoading: false,
  playerState: '',
  currentPosition: 0, // ms
  isCardAutoplayOn: false,
  cardSleepNum: 0,
  isStackAutoplayOn: true,
  stackSleepNum: 100, // 0 = Off, 100 = End of stack
  sleepOptions: [3, 5, 10],
  playedTrackIds: [],
  audioById: {},
  defaultAudioTag: 'm_bg_mp3', // See Tags defined in AdminEditAudio.vue
  isDataLoaded: false,
};

/*

  GETTERS

 */

const getters = {
  isAutoplayOn: (state, getters, rootState) => {
    const { isCardAutoplayOn, isStackAutoplayOn } = state;
    const { routeName } = rootState.ui;
    return routeName === 'AudioStack' ? isStackAutoplayOn : isCardAutoplayOn;
  },

  sleepNum: (state, getters, rootState) => {
    const { cardSleepNum, stackSleepNum } = state;
    const { routeName } = rootState.ui;
    return routeName === 'AudioStack' ? stackSleepNum : cardSleepNum;
  },

  trackHasBeenPlayed: state => {
    const { currentTrackId, playedTrackIds } = state;
    return currentTrackId === playedTrackIds[playedTrackIds.length - 1];
  },
};

/*

  ACTIONS

 */

const actions = {
  // Dispatched in user.js -> init
  async init({ state, dispatch }) {
    if (state.isDataLoaded) return;
    dispatch('getAudioDataFromDb');
    dispatch('addPluginEventListeners');
    dispatch('getLocalState');
  },

  async getAudioDataFromDb({ commit }) {
    try {
      const audioData = {};
      const querySnapshot = await fb.audioCollection.get();
      querySnapshot.forEach(doc => {
        const { id } = doc;
        const data = doc.data();
        audioData[id] = data;
      });
      commit('setAudio', audioData);
    } catch (error) {
      console.error('Error getting audio', error);
    }
  },

  async getLocalState({ commit }) {
    const sleepOptions = await localStore.getItem('sleepOptions');
    if (sleepOptions) {
      commit('setSleepOptions', sleepOptions);
    }
  },

  // REACT TO PLAYER EVENTS

  addPluginEventListeners({ commit, dispatch, state, getters }) {
    window.addEventListener('audioPositionUpdate', ({ position }) => {
      commit('setCurrentPosition', position);
      // Track considered played after 30s and is added to played track list
      if (position > 30000 && !getters.trackHasBeenPlayed) {
        dispatch('updatePlayedTracks');
      }
    });

    window.addEventListener('audioStatusUpdate', data => {
      // not currently using  playbackState
      const { isPlaying, position, currentTrackId, currentTrackIndex, hasPrevious, hasNext } = data;
      commit('setIsPlaying', isPlaying);
      commit('setCurrentPosition', position);
      // Track changes
      if (state.currentTrackId !== currentTrackId) {
        dispatch('updateCurrentTrackId', { currentTrackId, currentTrackIndex });
        if (!hasPrevious) {
          dispatch('addTrack', true);
        }
        if (!hasNext) {
          dispatch('addTrack', false);
        }
      }
    });

    window.addEventListener('audioLoadingUpdate', ({ isLoading }) => {
      commit('setIsLoading', isLoading);
    });

    window.addEventListener('audioPlayerStopped', () => {
      dispatch('clearPlayerState'); // Change to dispatch to reset sleep num if needed
    });

    window.addEventListener('audioPlayerError', ({ error }) => {
      console.error('ERROR: ', error);
      // TODO do something with error
    });
  },

  // There should always be a next and previous track available
  // So add track to player if current track doesn't have previous or next
  addTrack({ state, rootState, commit }, isPrevious) {
    const { currentTrackId, audioById, playlistIds, defaultAudioTag } = state;
    const { currentSet } = rootState.ui;
    const { allCards } = rootState.cards;

    const currentIndex = currentSet.indexOf(currentTrackId);
    let trackIndex;
    if (isPrevious) {
      trackIndex = currentIndex === 0 ? currentSet.length - 1 : currentIndex - 1;
    } else {
      trackIndex = currentIndex === currentSet.length - 1 ? 0 : currentIndex + 1;
    }
    const trackId = currentSet[trackIndex];

    if (trackId) {
      const track = getTrackData({ id: trackId, allCards, audioById, defaultAudioTag });
      try {
        AudioPlugin.addTrack({
          isPrevious,
          trackData: JSON.stringify(track),
        });
      } catch (error) {
        console.warn('Error adding  track', error);
      }
      if (isPrevious) {
        commit('setPlaylistIds', [trackId, ...playlistIds]);
      } else {
        commit('setPlaylistIds', [...playlistIds, trackId]);
      }
    }
  },

  // If player changes tracks, update route to match
  updateCurrentTrackId({ rootState, commit }, { currentTrackId, currentTrackIndex }) {
    commit('setCurrentTrackInfo', { currentTrackId, currentTrackIndex });

    const { routeName, currentId } = rootState.ui;

    if (routeName === 'AudioCard' && currentId !== currentTrackId) {
      router.replace({
        name: 'AudioCard',
        params: { id: currentTrackId },
      });
      commit('ui/setCurrentIndexForId', currentTrackId, { root: true });
    }
  },

  // Update list of tracks that have been played
  // If sleep timer is on, decrement sleepNum (unless set to 100 = end of stack)
  // If only 1 card left in sleep timer, or at the end of stack
  // send message to stop after track ends
  // If accidentally left auto-playing, stop after 50 tracks
  updatePlayedTracks({ state, getters, commit, dispatch }) {
    commit('addPlayedTrackId', state.currentTrackId);
    const { currentTrackIndex, playlistIds, playedTrackIds } = state;
    const { sleepNum } = getters;

    analytics.trackPlayed();

    if (sleepNum < 1) return;
    if (
      sleepNum === 1 ||
      playedTrackIds.length > 50 ||
      (sleepNum === 100 && currentTrackIndex === playlistIds.length - 1)
    ) {
      dispatch('setStopAfterTrack', true);
      // console.log('Stop after this track');
    }
    if (sleepNum < 100) {
      dispatch('updateSleepNum', sleepNum - 1);
    }
  },

  //
  // REACT TO UI ACTIONS
  //

  // Dispatched by ui/parseRoute if the previous route was an Audio View
  handleRouteChange({ state, rootState, dispatch }, { isAudioView, routeName }) {
    const { currentTrackId } = state;
    const { currentId } = rootState.ui;

    // Left Audio View ==> Stop player & reset player state
    if (!isAudioView) {
      // console.log('STOPING AUDIO - no longer on Audio View');
      dispatch('stop');
      return;
    }

    // On AudioCard route & on right card => Do nothing
    if (routeName === 'AudioCard' && currentTrackId === currentId) {
      // console.log('handleRouteChange - already on right card', { currentTrackId, currentId });
      return;
    }

    // In Audio View with player active, & ID changes (user changes card/stack)
    //  => Stop player & reset player state
    // (UI gets janky if trying to keep player in sync and add a track for every swipe)
    if (currentTrackId) {
      // console.log('STOPPING - else', { currentTrackId, currentId });
      dispatch('stop');
    }
  },

  handlePlayPauseClick({ state, rootState, dispatch }) {
    const { currentTrackId, isPlaying, isLoading } = state;
    const { currentId } = rootState.ui;

    if (isLoading) return;

    try {
      if (isPlaying) {
        AudioPlugin.pause();
      } else if (currentId !== currentTrackId) {
        dispatch('playNewPlaylist', {});
      } else {
        AudioPlugin.resume();
      }
    } catch (error) {
      console.warn('handlePlayPauseClick', error);
    }
  },

  // Index will be null if main play/pause button clicked
  handleStackPlayPauseClick({ state, dispatch }, { cardIds, index = null }) {
    const { currentTrackId, currentTrackIndex, isPlaying, isLoading } = state;

    if (isLoading) return;

    try {
      if (!currentTrackId) {
        dispatch('playNewPlaylist', { cardIds, index: index || 0 });
      } else if (index !== null && index !== currentTrackIndex) {
        AudioPlugin.skipToTrack({ index });
      } else if (!isPlaying) {
        AudioPlugin.resume();
      } else {
        AudioPlugin.pause();
      }
    } catch (error) {
      console.warn('handleStackPlayPauseClick', error);
    }
  },

  // If playing a stack, include cardIds
  playNewPlaylist({ state, getters, rootState, commit }, { cardIds = null, index = null }) {
    commit('setIsPlaying', true);
    const { audioById, defaultAudioTag } = state;
    const { isAutoplayOn, sleepNum } = getters;
    const { currentIndex, currentSet } = rootState.ui;
    const { allCards } = rootState.cards;
    const isStack = !!cardIds;

    let targetLength = sleepNum > 0 ? sleepNum + 1 : 3; // 3 = prev + current + next
    if (cardIds) {
      targetLength = cardIds.length;
    }

    // Default playlist: [previous, current, next]
    // If sleep is on, include all cards until sleep
    const { playlistIds, playlistData } = generatePlaylist({
      set: cardIds || currentSet,
      startIndex: isStack ? 0 : currentIndex - 1,
      targetLength,
      audioById,
      allCards,
      defaultAudioTag,
    });
    // console.log(playlistIds);
    commit('setPlaylistIds', playlistIds);

    // Android audio player blocks UI while it's preparing,
    // so giving button ripple time to finish first
    setTimeout(() => {
      try {
        AudioPlugin.playNewPlaylist({
          currentTrackIndex: cardIds ? index : 1,
          playlistIds,
          playlistData: JSON.stringify(playlistData),
          stopAfterTrack: !isAutoplayOn,
          loopPlaylist: isStack,
        });
      } catch (error) {
        console.warn('Error in playNewPlaylist', error);
      }
    }, 420);
  },

  next() {
    try {
      AudioPlugin.next();
    } catch (e) {
      console.warn('next error:', e);
    }
  },

  previous() {
    try {
      AudioPlugin.previous();
    } catch (e) {
      console.warn('previous error:', e);
    }
  },

  updateAutoplay({ commit, dispatch, rootState }, isAutoplayOn) {
    const { routeName } = rootState.ui;
    commit(`setIs${routeName === 'AudioStack' ? 'Stack' : 'Card'}AutoplayOn`, isAutoplayOn);
    dispatch('setStopAfterTrack', !isAutoplayOn);
  },

  updateSleepNum({ commit, dispatch, getters, rootState }, num = null) {
    const { isAutoplayOn } = getters;
    const isStack = rootState.ui.routeName === 'AudioStack';
    const defaultSleepNum = isStack ? 100 : 0;
    const sleepNum = num === null ? defaultSleepNum : num;
    commit(`set${isStack ? 'Stack' : 'Card'}SleepNum`, sleepNum);

    // If sleep num isn't 0, turn on auto-play
    if (!isAutoplayOn && sleepNum > 0) {
      setTimeout(() => {
        dispatch('updateAutoplay', true);
      }, 200);
    }
  },

  handleSleepOptionChange({ commit, state }, { index, num }) {
    const newSleepOptions = [...state.sleepOptions];
    newSleepOptions[index] = num;
    commit('setSleepOptions', newSleepOptions);
    localStore.setItem('sleepOptions', newSleepOptions);
  },

  setStopAfterTrack(context, bool) {
    // console.log('Setting stopAfterTrack', bool);
    try {
      AudioPlugin.setStopAfterTrack({ stopAfterTrack: bool });
    } catch (error) {
      console.warn('setStopAfterTrack', error);
    }
  },

  stop({ dispatch }) {
    dispatch('clearPlayerState');
    try {
      AudioPlugin.stop();
    } catch (error) {
      console.warn('stop', error);
    }
  },

  clearPlayerState({ commit }) {
    commit('resetPlayerState');
  },
};

/*

  MUTATIONS

 */

const mutations = {
  setAudio(state, data) {
    state.audioById = data;
    state.isDataLoaded = true;
  },

  setIsLoading(state, isLoading) {
    state.isLoading = isLoading;
  },

  setCurrentPosition(state, position) {
    state.currentPosition = position || 0;
  },

  setPlaylistIds(state, ids) {
    state.playlistIds = ids;
  },

  setIsPlaying(state, isPlaying) {
    state.isPlaying = isPlaying;
    if (isPlaying) {
      state.isLoading = false;
    }
  },

  setCurrentTrackInfo(state, { currentTrackId, currentTrackIndex }) {
    state.currentTrackId = currentTrackId;
    state.currentTrackIndex = +currentTrackIndex;
  },

  addPlayedTrackId(state, currentTrackId) {
    state.playedTrackIds.push(currentTrackId);
  },

  setIsCardAutoplayOn(state, bool) {
    state.isCardAutoplayOn = bool;
  },

  setIsStackAutoplayOn(state, bool) {
    state.isStackAutoplayOn = bool;
  },

  setCardSleepNum(state, num) {
    state.cardSleepNum = +num;
  },

  setStackSleepNum(state, num) {
    state.stackSleepNum = +num;
  },

  setSleepOptions(state, sleepOptions) {
    state.sleepOptions = sleepOptions;
  },

  // Called by cancelTimer in AudioPlayerSettings
  // Also reset in player state below
  resetPlayedTrackIds(state) {
    state.playedTrackIds = [];
  },

  resetPlayerState(state) {
    state.playlistIds = [];
    state.currentTrackId = '';
    state.currentTrackIndex = 0;
    state.isPlaying = false;
    state.isLoading = false;
    state.playerState = '';
    state.currentPosition = 0;
    state.trackDuration = 0;
    state.playedTrackIds = [];
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
